// components/DeliverySession/DeliverySessionList.tsx
import React, { useState, useMemo } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import './index.css';
import { DeliverySession } from '../../../../../hooks/useDeliverySessionData';
import DeliverySessionDetail from './DeliverySessionDetail';
import { formatDate } from '../../../../../utility/Utility';
import { Spinner, Form } from 'react-bootstrap';
import { useCartDeliverySessions } from '../../../../../hooks/useDeliverySessionData';

interface DeliverySessionListProps {
  cartId: string;
  cartName?: string;
  selectedDate?: string;
}

const formatDateSafe = (timestamp: any): string => {
  if (!timestamp) return 'N/A';
  try {
    if (timestamp?.toDate) {
      const date = timestamp.toDate();
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    }
    if (typeof timestamp === 'string') return formatDate(timestamp);
    return String(timestamp);
  } catch {
    return 'N/A';
  }
};

const getCurrentDate = (): string => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (dateString: string): string => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const sortSessionsByCreatedTime = (sessions: DeliverySession[]): DeliverySession[] => {
  return [...sessions].sort((a, b) => {
    const getTime = (session: DeliverySession): number => {
      if (session.createdAt?.toDate) return session.createdAt.toDate().getTime();
      if (session.createdAt?.seconds) return session.createdAt.seconds * 1000;
      return new Date(session.createdAt).getTime() || 0;
    };
    return getTime(a) - getTime(b);
  });
};

const filterSessionsByDate = (sessions: DeliverySession[], filterDate: string): DeliverySession[] => {
  if (!filterDate) return sessions;

  const startOfDay = new Date(filterDate);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(filterDate);
  endOfDay.setHours(23, 59, 59, 999);

  return sessions.filter(session => {
    let sessionDate: Date | null = null;
    if (session.createdAt?.toDate) sessionDate = session.createdAt.toDate();
    else if (session.createdAt?.seconds) sessionDate = new Date(session.createdAt.seconds * 1000);
    else if (session.createdAt) sessionDate = new Date(session.createdAt);
    if (!sessionDate) return false;
    return sessionDate >= startOfDay && sessionDate <= endOfDay;
  });
};

const DeliverySessionList: React.FC<DeliverySessionListProps> = ({
  cartId,
  cartName,
  selectedDate: initialSelectedDate,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [localSelectedSession, setLocalSelectedSession] = useState<DeliverySession | null>(null);
  const [filterDate, setFilterDate] = useState<string>(initialSelectedDate || getCurrentDate());

  const {
    deliverySessions,
    selectedDeliverySession,
    groupOrders,
    loadingSessions,
    loadingSessionDetail,
    error,
    selectDeliverySession,
    clearSelectedSession,
    setDateFilter,
  } = useCartDeliverySessions(cartId, filterDate);

  const sortedSessions = useMemo(
    () => sortSessionsByCreatedTime(deliverySessions),
    [deliverySessions]
  );

  const filteredSessions = useMemo(
    () => filterSessionsByDate(sortedSessions, filterDate),
    [sortedSessions, filterDate]
  );

  const filteredSummary = useMemo(() => {
    const activeSessions = filteredSessions.filter(s => s.isActive).length;
    const completedSessions = filteredSessions.filter(s => !s.isActive).length;
    
    const totalDeliveryDistance = filteredSessions
      .reduce((sum, s) => sum + (s.deliveryDistance || 0), 0);
    
    const totalCalculatedDistance = filteredSessions
      .reduce((sum, s) => sum + (s.totalDistance || 0), 0);
    
    const variance = totalCalculatedDistance - totalDeliveryDistance;
    const variancePercentage = totalDeliveryDistance > 0 
      ? ((variance / totalDeliveryDistance) * 100).toFixed(1)
      : '0';

    return {
      totalSessions: filteredSessions.length,
      activeSessions,
      completedSessions,
      totalDeliveryDistance: totalDeliveryDistance.toFixed(1),
      totalCalculatedDistance: totalCalculatedDistance.toFixed(1),
      variance: variance.toFixed(1),
      variancePercentage,
    };
  }, [filteredSessions]);

  const handleDateChange = (date: string) => {
    setFilterDate(date);
    setDateFilter(date);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
  };

  const handleView = (session: DeliverySession) => {
    selectDeliverySession(session.id);
  };

  const handleBackToList = () => {
    clearSelectedSession();
    setLocalSelectedSession(null);
  };

  const getStatusBadge = (isActive: boolean) =>
    isActive ? (
      <span className="delivery-session-container__status-badge delivery-session-container__status-badge--active">
        Active
      </span>
    ) : (
      <span className="delivery-session-container__status-badge delivery-session-container__status-badge--done">
        Inactive
      </span>
    );

  const columns: TableColumn<DeliverySession>[] = [
    {
      name: <span className="delivery-session-container__header-cell">Session</span>,
      selector: row => row.id,
      sortable: false,
      cell: (row, index) => (
        <div className="delivery-session-container__cell">
          <span className="delivery-session-container__session-id">
            Session {index + 1}
          </span>
          <span className="delivery-session-container__session-date">
            ID: {row.id.slice(0, 8)}...
          </span>
        </div>
      ),
      width: '200px',
    },
    {
      name: <span className="delivery-session-container__header-cell">Created At</span>,
      selector: row => row.createdAt,
      sortable: true,
      cell: row => (
        <div className="delivery-session-container__cell">
          <span className="delivery-session-container__session-id">
            {formatDateSafe(row.createdAt)}
          </span>
        </div>
      ),
      width: '300px',
    },
    {
      name: <span className="delivery-session-container__header-cell">Delivery Distance</span>,
      selector: row => row.deliveryDistance,
      sortable: true,
      cell: row => (
        <div className="delivery-session-container__cell">
          <span className="delivery-session-container__distance-number delivery-session-container__distance-number--delivery">
            {row.deliveryDistance}
          </span>
          <span className="delivery-session-container__distance-unit">km</span>
        </div>
      ),
      width: '190px',
    },
    {
      name: <span className="delivery-session-container__header-cell">Total Distance</span>,
      selector: row => row.totalDistance,
      sortable: true,
      cell: row => (
        <div className="delivery-session-container__cell">
          <span className="delivery-session-container__distance-number delivery-session-container__distance-number--total">
            {row.totalDistance}
          </span>
          <span className="delivery-session-container__distance-unit">km</span>
        </div>
      ),
      width: '200px',
    },
    {
      name: <span className="delivery-session-container__header-cell">Points</span>,
      selector: row => row.locationPoints?.length || 0,
      sortable: true,
      cell: row => (
        <div className="delivery-session-container__cell">
          <span className="delivery-session-container__points-count">
            {row.locationPoints?.length || 0}
          </span>
          <span className="delivery-session-container__points-label">geopoints</span>
        </div>
      ),
      width: '200px',
    },
    {
      name: <span className="delivery-session-container__header-cell">Status</span>,
      selector: row => (row.isActive ? 'Active' : 'Inactive'),
      sortable: true,
      cell: row => getStatusBadge(row.isActive),
      width: '200px',
    },
    {
      name: <span className="delivery-session-container__header-cell delivery-session-container__header-cell--action">Action</span>,
      cell: row => (
        <button 
          className="delivery-session-container__view-button"
          onClick={() => handleView(row)}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          View
        </button>
      ),
      width: '200px',
    },
  ];

  if (selectedDeliverySession) {
    const groupOrder = selectedDeliverySession.groupOrderId
      ? groupOrders.get(selectedDeliverySession.groupOrderId)
      : null;

    return (
      <DeliverySessionDetail
        session={selectedDeliverySession}
        groupOrder={groupOrder}
        onBack={handleBackToList}
        loading={loadingSessionDetail}
        filterDate={filterDate}
      />
    );
  }

  if (loadingSessions && deliverySessions.length === 0) {
    return (
      <div className="delivery-session-container text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading delivery sessions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="delivery-session-container text-center p-5 text-danger">
        <p>Error loading delivery sessions: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="delivery-session-container">
      <div className="delivery-session-container__header">
        <div>
          <h3 className="delivery-session-container__title">
            {cartName ? `${cartName} - Delivery Sessions` : 'Delivery Sessions'}
          </h3>
          <span className="delivery-session-container__count">
            {filteredSessions.length} Sessions
          </span>
        </div>
        <div>
          <Form.Group className="d-flex align-items-center gap-3">
            <Form.Label className="fw-semibold mb-0">Select Date:</Form.Label>
            <Form.Control
              type="date"
              value={filterDate}
              onChange={e => handleDateChange(e.target.value)}
              style={{ width: 'auto', display: 'inline-block' }}
              className="form-control-sm"
            />
          </Form.Group>
        </div>
      </div>

      <div className="delivery-session-container__summary-grid">
        <div className="delivery-session-container__summary-card delivery-session-container__summary-card--delivery">
          <div className="delivery-session-container__summary-label">
            Total Delivery Distance
          </div>
          <div className="delivery-session-container__summary-number">
            {filteredSummary.totalDeliveryDistance}
          </div>
          <div className="delivery-session-container__summary-note">
            km on {formatDisplayDate(filterDate)}
          </div>
        </div>

        <div className="delivery-session-container__summary-card delivery-session-container__summary-card--calculated">
          <div className="delivery-session-container__summary-label">
            Total Calculated Distance
          </div>
          <div className="delivery-session-container__summary-number">
            {filteredSummary.totalCalculatedDistance}
          </div>
          <div className="delivery-session-container__summary-note">
            km on {formatDisplayDate(filterDate)}
          </div>
        </div>
      </div>

      <div className="delivery-session-container__table-wrapper">
        <DataTable
          columns={columns}
          data={filteredSessions}
          responsive
          pagination
          highlightOnHover
          paginationPerPage={rowsPerPage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          progressPending={loadingSessions}
          progressComponent={<div className="text-center p-4">Loading sessions...</div>}
          noDataComponent={
            <div className="text-center p-4">
              <p className="mb-2">No delivery sessions found</p>
              <small className="text-muted">
                No sessions found for {formatDisplayDate(filterDate)}
              </small>
            </div>
          }
          customStyles={{
            headCells: {
              style: {
                backgroundColor: '#f8fafc',
                padding: '12px 20px',
                borderBottom: '1px solid #e2e8f0',
              },
            },
            rows: {
              style: {
                minHeight: '72px',
              },
            },
            cells: {
              style: {
                padding: '16px 20px',
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default DeliverySessionList;